import React, { useState, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';

import { authenticationAtom } from '../../State';
import { storageAPI } from '../../StorageAPI';
import FileTreePicker from './FileTreePicker';
import { collectFieldFileIds } from './zodEditorFileIds';
import { dateitypLesen, dateitypSpeichern } from './zodEditorFileType';

import {
    Calendar,
    Check,
    CheckSquare,
    ChevronsUpDown,
    FileSpreadsheet,
    FileText,
    Folder,
    Hash,
    Link2,
    List,
    Pencil,
    Trash2,
    Type,
    Upload,
    X,
    Zap,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';

// ZodField interface matching the one from tri-model (for external use)
interface ZodField {
    id: string;
    name: string;
    type: 'string' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'file' | 'array' | 'object';
    required: boolean;
    description: string;
    defaultValue: string;
    children: ZodField[];
    arrayType?: 'string' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'file' | 'object';
    /**
     * Welche Art Datei — bei EINEM Datei-Feld steckt sie historisch in `arrayType`, weil dort nichts
     * anderes stand. Eine **Liste von Dateien** hat diesen Platz besetzt (`arrayType: 'file'`), also
     * braucht sie ein eigenes Feld; sonst gibt es für sie keinen Dateityp und die Auswahl im Editor
     * hätte nichts, worauf sie schreiben könnte. Beim Lesen gilt weiterhin beides.
     */
    fileType?: 'markdown' | 'text' | 'image' | 'pdf' | 'excel' | 'word' | 'powerpoint' | 'any';
    templateFile?: string; // Excel template file ID for output file fields
    hasPopulationInstructions?: boolean; // Whether this file field has population instructions
}

// Extended ZodField interface for the editor with additional properties and optional fields
interface ExtendedZodField {
    id: string;
    name: string;
    type: 'string' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'file' | 'array' | 'object';
    required: boolean;
    description: string;
    defaultValue: string;
    children: ExtendedZodField[];
    arrayType?: 'string' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'file' | 'object';
    isArray?: boolean;
    fileType?: 'markdown' | 'text' | 'image' | 'pdf' | 'excel' | 'word' | 'powerpoint' | 'any';
    templateFile?: string; // Excel template file ID for output file fields
    hasPopulationInstructions?: boolean; // Whether this file field has population instructions
}

// Conversion functions between ZodField and ExtendedZodField
const convertToExtended = (field: ZodField): ExtendedZodField => {
    // Beide Plätze an einer Stelle gelesen — siehe zodEditorFileType.ts. Die frühere Rechnung stand
    // dreimal im Protokoll und einmal im Ergebnis; das ist genau die Stelle, an der beides
    // auseinanderläuft, sobald jemand nur eine davon anfasst.
    const gelesenerDateityp = dateitypLesen(field);

    const result = {
        ...field,
        description: field.description || '',
        defaultValue: field.defaultValue || '',
        children: field.children || [],
        isArray: false,
        // Extract fileType from arrayType if it's a file field, or use existing fileType if available
        fileType: gelesenerDateityp,
        // Preserve templateFile for Excel output fields
        templateFile: field.templateFile || '',
        hasPopulationInstructions: field.hasPopulationInstructions ?? false
    };
    
    console.log('🔍 convertToExtended:', {
        name: field.name,
        type: field.type,
        arrayType: field.arrayType,
        fileType: field.fileType,
        gelesen: gelesenerDateityp,
    });
    return result;
};

const convertToStandard = (field: ExtendedZodField): ZodField => {
    const { arrayType, fileType } = dateitypSpeichern(field);
    const result = {
        id: field.id,
        name: field.name,
        type: field.type,
        required: field.required,
        description: field.description,
        defaultValue: field.defaultValue,
        children: field.children,
        /*
         * Beide Plätze an einer Stelle geschrieben — siehe zodEditorFileType.ts. Die Umdeutung
         * ist nötig, weil der alte Platz bei einem einzelnen Datei-Feld einen Dateityp trägt
         * („pdf"), der in der deklarierten Liste der Elementtypen nicht vorkommt. Genau dafür
         * stand hier vorher ein `as any` über dem ganzen Ausdruck.
         */
        arrayType: arrayType as ZodField['arrayType'],
        fileType,
        // Include templateFile for Excel output fields
        templateFile: field.templateFile || undefined,
        hasPopulationInstructions: field.hasPopulationInstructions || undefined
    };
    console.log('convertToStandard:', {
        input: { name: field.name, type: field.type, fileType: field.fileType, arrayType: field.arrayType, templateFile: field.templateFile },
        output: { name: result.name, type: result.type, arrayType: result.arrayType, templateFile: result.templateFile }
    });
    return result;
};

interface ComboBoxOption {
    key: any;
    text: string;
}

/**
 * Ersetzt die abgelöste ComboBox: Auswahlfeld mit Tippsuche (Popover + cmdk).
 * Meldet wie zuvor die gewählte Option zurück, ohne selbst Zustand zu halten.
 */
function ComboBoxField({
    id,
    label,
    options,
    selectedKey,
    onSelect,
    placeholder,
    required,
    emptyText,
}: {
    id: string;
    label: string;
    options: ComboBoxOption[];
    selectedKey?: any;
    onSelect: (key: any) => void;
    placeholder: string;
    required?: boolean;
    emptyText: string;
}) {
    const [open, setOpen] = useState(false);
    const selected = options.find(option => option.key === selectedKey);

    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={id}>
                {label}
                {required && <span className="ml-0.5 text-destructive">*</span>}
            </Label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                    >
                        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
                            {selected ? selected.text : placeholder}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                        <CommandInput placeholder={placeholder} />
                        <CommandList>
                            <CommandEmpty>{emptyText}</CommandEmpty>
                            <CommandGroup>
                                {options.map(option => (
                                    <CommandItem
                                        key={String(option.key)}
                                        value={option.text}
                                        onSelect={() => {
                                            onSelect(option.key);
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                'mr-2 h-4 w-4',
                                                option.key === selectedKey ? 'opacity-100' : 'opacity-0',
                                            )}
                                            aria-hidden
                                        />
                                        <span className="truncate">{option.text}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}

interface ZodEditorProps {
    value: string;
    onChange: (value: string, fields: ZodField[]) => void;
    label?: string;
    placeholder?: string;
    initialFields?: ZodField[];
    debugName?: string; // Add debug name to distinguish instances
    isOutputSchema?: boolean; // Whether this is an output schema (enables templateFile for Excel fields)
}

interface ZodEditorRef {
    getCurrentSchema: () => { schema: string; fields: ZodField[] };
}

const ZodEditor = forwardRef<ZodEditorRef, ZodEditorProps>(({ value: _value, onChange, label, placeholder: _placeholder, initialFields, debugName, isOutputSchema }: ZodEditorProps, ref: React.Ref<ZodEditorRef>) => {
    const { t } = useTranslation();
    
    // Memoize the initial fields to prevent re-initialization
    const memoizedInitialFields = useMemo(() => {
        console.log(`ZodEditor [${debugName || 'UNKNOWN'}]: Memoizing initialFields:`, JSON.stringify(initialFields, null, 2));
        if (!initialFields || initialFields.length === 0) {
            console.log(`ZodEditor [${debugName || 'UNKNOWN'}]: No initialFields, returning empty array`);
            return [];
        }
        console.log(`ZodEditor [${debugName || 'UNKNOWN'}]: Converting ${initialFields.length} fields...`);
        const convertedFields = initialFields.map((field, index) => {
            console.log(`ZodEditor [${debugName || 'UNKNOWN'}]: Converting field ${index}:`, JSON.stringify(field, null, 2));
            const converted = convertToExtended(field);
            console.log(`ZodEditor [${debugName || 'UNKNOWN'}]: Converted field ${index}:`, JSON.stringify(converted, null, 2));
            return converted;
        });
        console.log(`ZodEditor [${debugName || 'UNKNOWN'}]: All converted fields:`, JSON.stringify(convertedFields, null, 2));
        return convertedFields;
    }, [initialFields, debugName]);
    
    const [fields, setFields] = useState<ExtendedZodField[]>(memoizedInitialFields);
    
    // Debug: Log the fields state when it changes
    React.useEffect(() => {
        console.log('🔍 ZodEditor fields state updated:', JSON.stringify(fields, null, 2));
        fields.forEach((field, index) => {
            console.log(`🔍 Field ${index} fileType:`, field.fileType);
        });
    }, [fields]);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOverField, setDragOverField] = useState<string | null>(null);
    const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
    const [newField, setNewField] = useState<Partial<ExtendedZodField>>({ fileType: 'any' });
    const [editingField, setEditingField] = useState<ExtendedZodField | null>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const nameFieldRef = useRef<HTMLInputElement>(null);
    const [nextFieldId, setNextFieldId] = useState(1);
    
    // Map to track file ID -> filename for display purposes (stores ID but shows filename)
    const [fileIdToName, setFileIdToName] = useState<Map<string, string>>(new Map());
    // File IDs that resolved to a 404 (the ManagedFile was deleted): shown as
    // "file not found" rather than a cryptic truncated id.
    const [missingFileIds, setMissingFileIds] = useState<Set<string>>(new Set());
    const [authentication] = useAtom(authenticationAtom) as any;

    // Resolve stored file IDs to filenames for display. The map is otherwise only
    // populated when the user picks a file in the current session, so reopening a
    // saved field — whose file may live in a subfolder, absent from the root
    // listing — would show the raw ID. Resolve each referenced id directly by id.
    const fileIdToNameRef = useRef(fileIdToName);
    fileIdToNameRef.current = fileIdToName;
    const missingFileIdsRef = useRef(missingFileIds);
    missingFileIdsRef.current = missingFileIds;
    React.useEffect(() => {
        if (!authentication?.customer) return;
        const unresolved = collectFieldFileIds(fields).filter(
            id => id && !fileIdToNameRef.current.has(id) && !missingFileIdsRef.current.has(id)
        );
        if (unresolved.length === 0) return;
        let cancelled = false;
        Promise.all(
            unresolved.map(id =>
                storageAPI.getFileMeta(authentication.customer, id)
                    // string => resolved name, null => 404 (file gone), undefined => transient error
                    .then(meta => [id, meta ? meta.name : null] as const)
                    .catch(() => [id, undefined] as const)
            )
        ).then(results => {
            if (cancelled) return;
            const resolved = results.filter(([, v]) => typeof v === 'string') as [string, string][];
            const notFound = results.filter(([, v]) => v === null).map(([id]) => id);
            if (resolved.length > 0) {
                setFileIdToName(prev => {
                    const newMap = new Map(prev);
                    resolved.forEach(([id, name]) => newMap.set(id, name));
                    return newMap;
                });
            }
            if (notFound.length > 0) {
                setMissingFileIds(prev => {
                    const newSet = new Set(prev);
                    notFound.forEach(id => newSet.add(id));
                    return newSet;
                });
            }
        });
        return () => { cancelled = true; };
    }, [fields, authentication?.customer]);

    // Expose getCurrentSchema method to parent components

    // Generate unique field ID with timestamp to ensure uniqueness
    const generateFieldId = () => {
        const id = `field-${Date.now()}-${nextFieldId}`;
        setNextFieldId(nextId => nextId + 1);
        return id;
    };

    const fieldTypes = [
        { key: 'string', text: 'String', Icon: Type },
        { key: 'number', text: 'Number', Icon: Hash },
        { key: 'boolean', text: 'Boolean', Icon: CheckSquare },
        { key: 'array', text: 'Array', Icon: List },
        { key: 'object', text: 'Object', Icon: Folder },
        { key: 'date', text: 'Date', Icon: Calendar },
        { key: 'url', text: 'URL', Icon: Link2 },
        { key: 'file', text: 'File', Icon: FileText }
    ];

    const getNextFieldNumber = (type: string, fieldList: ExtendedZodField[]): number => {
        const existingFields = fieldList.filter(field => field.name.startsWith(`${type}-`));
        if (existingFields.length === 0) return 1;
        
        const numbers = existingFields.map(field => {
            const match = field.name.match(new RegExp(`^${type}-(\\d+)$`));
            return match ? parseInt(match[1]) : 0;
        });
        
        return Math.max(...numbers) + 1;
    };

    const generateZodSchema = (fields: ExtendedZodField[], indentLevel: number = 0): string => {
        if (fields.length === 0) return '';
        
        const indent = '  '.repeat(indentLevel);
        const nextIndent = '  '.repeat(indentLevel + 1);
        
        const generateFieldSchema = (field: ExtendedZodField, level: number = 0): string => {
            let schema = '';
            
            switch (field.type) {
                case 'string':
                    schema = 'z.string()';
                    break;
                case 'number':
                    schema = 'z.number()';
                    break;
                case 'boolean':
                    schema = 'z.boolean()';
                    break;
                case 'date':
                    schema = 'z.date()';
                    break;
                case 'url':
                    schema = 'z.string().url()';
                    break;
                case 'file':
                    // Check both fileType and arrayType (for backward compatibility)
                    const fileType = field.fileType || (field.arrayType && ['markdown', 'text', 'image', 'pdf', 'excel', 'word', 'powerpoint', 'any'].includes(field.arrayType as string) ? field.arrayType as string : 'any');
                    // Use z.custom() for Zod v3 compatibility
                    if (fileType === 'markdown') {
                        schema = 'z.custom((val) => val && (val.type === "text/markdown" || val.name?.endsWith(".md")), "File must be a markdown file")';
                    } else if (fileType === 'text') {
                        schema = 'z.custom((val) => val && val.type?.startsWith("text/"), "File must be a text file")';
                    } else if (fileType === 'image') {
                        schema = 'z.custom((val) => val && val.type?.startsWith("image/"), "File must be an image file")';
                    } else if (fileType === 'pdf') {
                        schema = 'z.custom((val) => val && val.type === "application/pdf", "File must be a PDF file")';
                    } else if (fileType === 'excel') {
                        schema = 'z.custom((val) => val && (val.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || val.name?.endsWith(".xlsx")), "File must be an Excel file (.xlsx)")';
                    } else if (fileType === 'word') {
                        schema = 'z.custom((val) => val && (val.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || val.name?.endsWith(".docx")), "File must be a Word document (.docx)")';
                    } else if (fileType === 'powerpoint') {
                        schema = 'z.custom((val) => val && (val.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation" || val.name?.endsWith(".pptx")), "File must be a PowerPoint presentation (.pptx)")';
                    } else {
                        schema = 'z.custom((val) => true, "File")';
                    }
                    break;
                case 'array':
                    if (field.arrayType === 'string') {
                        schema = 'z.array(z.string())';
                    } else if (field.arrayType === 'number') {
                        schema = 'z.array(z.number())';
                    } else if (field.arrayType === 'boolean') {
                        schema = 'z.array(z.boolean())';
                    } else if (field.arrayType === 'date') {
                        schema = 'z.array(z.date())';
                    } else if (field.arrayType === 'url') {
                        schema = 'z.array(z.string().url())';
                    } else if (field.arrayType === 'file') {
                        // For file arrays, we'll use the same file type validation
                        const fileType = field.fileType || 'any';
                        // Use z.custom() for Zod v3 compatibility
                        if (fileType === 'markdown') {
                            schema = 'z.array(z.custom((val) => val && (val.type === "text/markdown" || val.name?.endsWith(".md")), "File must be a markdown file"))';
                        } else if (fileType === 'text') {
                            schema = 'z.array(z.custom((val) => val && val.type?.startsWith("text/"), "File must be a text file"))';
                        } else if (fileType === 'image') {
                            schema = 'z.array(z.custom((val) => val && val.type?.startsWith("image/"), "File must be an image file"))';
                        } else if (fileType === 'pdf') {
                            schema = 'z.array(z.custom((val) => val && val.type === "application/pdf", "File must be a PDF file"))';
                        } else if (fileType === 'excel') {
                            schema = 'z.array(z.custom((val) => val && (val.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || val.name?.endsWith(".xlsx")), "File must be an Excel file (.xlsx)"))';
                        } else if (fileType === 'word') {
                            schema = 'z.array(z.custom((val) => val && (val.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || val.name?.endsWith(".docx")), "File must be a Word document (.docx)"))';
                        } else if (fileType === 'powerpoint') {
                            schema = 'z.array(z.custom((val) => val && (val.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation" || val.name?.endsWith(".pptx")), "File must be a PowerPoint presentation (.pptx)"))';
                        } else {
                            schema = 'z.array(z.custom((val) => true, "File"))';
                        }
                    } else if (field.arrayType === 'object') {
                        if (field.children && field.children.length > 0) {
                            // Generate the object schema for the array elements
                            const objectSchema = generateZodSchema(field.children, level + 1);
                            schema = `z.array(${objectSchema})`;
                        } else {
                            schema = 'z.array(z.object({}))';
                        }
                    } else {
                        schema = 'z.array(z.string())';
                    }
                    break;
                case 'object':
                    if (field.children && field.children.length > 0) {
                        schema = generateZodSchema(field.children, level + 1);
                    } else {
                        schema = 'z.object({})';
                    }
                    break;
                default:
                    schema = 'z.string()';
            }

            if (field.description) {
                console.log('Adding description to schema:', field.description);
                schema += `.describe("${field.description}")`;
            }

            if (field.defaultValue !== undefined && field.defaultValue !== '') {
                if (field.type === 'string') {
                    schema += `.default("${field.defaultValue}")`;
                } else if (field.type === 'number') {
                    schema += `.default(${field.defaultValue})`;
                } else if (field.type === 'boolean') {
                    schema += `.default(${field.defaultValue})`;
                }
            }

            if (!field.required) {
                schema += '.optional()';
            }

            return schema;
        };

        const schemaFields = fields.map(field => 
            `${nextIndent}${field.name}: ${generateFieldSchema(field, indentLevel)}`
        ).join(',\n');

        return `z.object({\n${schemaFields}\n${indent}})`;
    };

    // Helper function to trigger onChange when fields are modified
    const triggerOnChange = () => {
        if (onChange) {
            const standardFields = fields.map(convertToStandard);
            const schemaString = generateZodSchema(fields);
            onChange(schemaString, standardFields);
        }
    };

    // Convert fields to JSON structure
    const _fieldsToJson = (fields: ExtendedZodField[]): any => {
        const result: any = {};
        
        fields.forEach(field => {
            let value: any;
            
            switch (field.type) {
                case 'string':
                    value = field.defaultValue || '';
                    break;
                case 'number':
                    value = field.defaultValue ? parseFloat(field.defaultValue) : 0;
                    break;
                case 'boolean':
                    value = field.defaultValue === 'true';
                    break;
                case 'object':
                    value = field.children && field.children.length > 0 ? _fieldsToJson(field.children) : {};
                    break;
                default:
                    value = '';
            }
            
            if (!field.required) {
                result[field.name] = value;
            } else {
                result[field.name] = value;
            }
        });
        
        return result;
    };

    const handleDragStart = (e: React.DragEvent, fieldType: string) => {
        e.dataTransfer.setData('fieldType', fieldType);
        setIsDragging(true);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        const target = e.target as HTMLElement;
        const fieldId = target.closest('[data-field-id]')?.getAttribute('data-field-id') || null;
        
        if (fieldId) {
            // Check if the target field is an object type
            const findTargetField = (fieldList: ExtendedZodField[]): ExtendedZodField | null => {
                for (const field of fieldList) {
                    if (field.id === fieldId) {
                        return field;
                    }
                    if (field.children) {
                        const found = findTargetField(field.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            
            const targetField = findTargetField(fields);
            
            // Allow dropping into object fields or array fields
            if (targetField && (targetField.type === 'object' || targetField.type === 'array')) {
                setDragOverField(fieldId);
            } else {
                setDragOverField(null);
            }
        } else {
            setDragOverField(null);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        setDragOverField(null);
        
        const fieldType = e.dataTransfer.getData('fieldType') as ZodField['type'];
        const target = e.target as HTMLElement;
        const targetFieldId = target.closest('[data-field-id]')?.getAttribute('data-field-id');
        
        if (targetFieldId) {
            // Find the target field to check its type
            const findTargetField = (fieldList: ZodField[]): ZodField | null => {
                for (const field of fieldList) {
                    if (field.id === targetFieldId) {
                        return field;
                    }
                    if (field.children) {
                        const found = findTargetField(field.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            
            const targetField = findTargetField(fields);
            
            // Add as child of target field or set array type
            const addFieldToParent = (parentId: string, newField: ExtendedZodField, fieldList: ExtendedZodField[]): ExtendedZodField[] => {
                return fieldList.map(field => {
                    if (field.id === parentId) {
                        if (field.type === 'object') {
                            // Drop into object field as child
                            return {
                                ...field,
                                children: [...(field.children || []), newField]
                            };
                        } else if (field.type === 'array') {
                            // Handle array fields
                            if (field.arrayType === 'object') {
                                // If array type is already 'object', add the new field as a child of the array
                                // This means the new field will be a field of each array element
                                return {
                                    ...field,
                                    children: [...(field.children || []), newField]
                                };
                            } else if (newField.type === 'object') {
                                // Set the array type to object
                                return {
                                    ...field,
                                    arrayType: 'object'
                                };
                                        } else if (['string', 'number', 'boolean', 'date', 'url', 'file'].includes(newField.type)) {
                // For primitive arrays, just set the type
                return {
                    ...field,
                    arrayType: newField.type as 'string' | 'number' | 'boolean' | 'date' | 'url' | 'file'
                };
                            } else {
                                // For unsupported types, default to string
                                return {
                                    ...field,
                                    arrayType: 'string'
                                };
                            }
                        } else {
                            // If target field is not an object or array, don't allow dropping
                            console.log('Cannot drop into field:', field.name, 'type:', field.type);
                            return field;
                        }
                    } else if (field.children) {
                        return {
                            ...field,
                            children: addFieldToParent(parentId, newField, field.children)
                        };
                    }
                    return field;
                });
            };
            
            const getNextNumberInTree = (type: string, fieldList: ExtendedZodField[]): number => {
                let maxNumber = 0;
                const findMaxNumber = (fields: ExtendedZodField[]) => {
                    fields.forEach(field => {
                        if (field.name.startsWith(`${type}-`)) {
                            const match = field.name.match(new RegExp(`^${type}-(\\d+)$`));
                            if (match) {
                                maxNumber = Math.max(maxNumber, parseInt(match[1]));
                            }
                        }
                        if (field.children) {
                            findMaxNumber(field.children);
                        }
                    });
                };
                findMaxNumber(fieldList);
                return maxNumber + 1;
            };
            
            // If we're dropping into an array and the field type is 'object', 
            // we don't create a new field, we just set the array type
            if (targetField && targetField.type === 'array' && fieldType === 'object') {
                // Just update the array type to object
                setFields(fields.map(field => 
                    field.id === targetFieldId 
                        ? { ...field, arrayType: 'object' }
                        : field
                ));
                triggerOnChange();
            } else {
                // Create a new field and add it to the parent
                const newField: ExtendedZodField = {
                    id: generateFieldId(),
                    name: `${fieldType}-${getNextNumberInTree(fieldType, fields)}`,
                    type: fieldType,
                    required: true,
                    description: '',
                    defaultValue: '',
                    children: []
                };
                
                setFields(addFieldToParent(targetFieldId, newField, fields));
                triggerOnChange();
            }
        } else {
            // Add to root level
            const newField: ExtendedZodField = {
                id: generateFieldId(),
                name: `${fieldType}-${getNextFieldNumber(fieldType, fields)}`,
                type: fieldType,
                required: true,
                description: '',
                defaultValue: '',
                children: []
            };
            setFields([...fields, newField]);
            triggerOnChange();
        }
    };

    const handleDragLeave = () => {
        setIsDragging(false);
        setDragOverField(null);
    };

    const addField = () => {
        if (newField.type) {
            const fieldName = newField.name || `${newField.type}-${getNextFieldNumber(newField.type, fields)}`;
            const field: ExtendedZodField = {
                id: generateFieldId(),
                name: fieldName,
                type: newField.type as ZodField['type'],
                required: newField.required || false,
                description: newField.description || '',
                defaultValue: newField.defaultValue || '',
                children: [],
                isArray: newField.isArray,
                arrayType: newField.arrayType as ZodField['arrayType'],
                fileType: newField.fileType,
                templateFile: newField.templateFile,
                hasPopulationInstructions: newField.hasPopulationInstructions || false
            };
            console.log('🔍 Adding field:', {
                name: field.name,
                type: field.type,
                fileType: field.fileType,
                arrayType: field.arrayType,
                templateFile: field.templateFile,
                newField: newField
            });
            setFields([...fields, field]);
            setNewField({});
            setAddFieldDialogOpen(false);
            triggerOnChange();
        }
    };

    const editField = (field: ExtendedZodField) => {
        console.log('🔍 editField called with field:', JSON.stringify(field, null, 2));
        setEditingField(field);
        const newFieldData = {
            name: field.name,
            type: field.type,
            required: field.required,
            description: field.description,
            defaultValue: field.defaultValue,
            isArray: field.isArray,
            arrayType: field.arrayType,
            fileType: field.fileType,
            templateFile: field.templateFile,
            hasPopulationInstructions: field.hasPopulationInstructions || false
        };
        console.log('🔍 editField setting newField to:', JSON.stringify(newFieldData, null, 2));
        setNewField(newFieldData);
        setAddFieldDialogOpen(true);
    };

    useImperativeHandle(ref, () => ({
        getCurrentSchema: () => {
            console.log(`🔍 ZodEditor [${debugName || 'UNKNOWN'}]: getCurrentSchema called`);
            console.log(`🔍 Current fields state:`, fields);
            fields.forEach((field, index) => {
                console.log(`🔍 Field ${index} details:`, {
                    id: field.id,
                    name: field.name,
                    type: field.type,
                    fileType: field.fileType,
                    arrayType: field.arrayType,
                    isArray: field.isArray
                });
            });
            const schema = generateZodSchema(fields);
            const standardFields = fields.map(convertToStandard);
            console.log(`🔍 Generated schema:`, schema);
            console.log(`🔍 Standard fields:`, standardFields);
            return { schema, fields: standardFields };
        },
        /**
         * **Ein Feld von außen zum Bearbeiten öffnen.**
         *
         * Die Kennzeichen im Reiter *Prompt* zeigen dieselben Felder, die hier bearbeitet werden —
         * wer eines anklickt, will genau dieses Feld ändern und nicht erst den Reiter wechseln und
         * es in der Liste wiederfinden. Gesucht wird nach Id und, falls die nicht passt, nach
         * Namen: Die Kennzeichen stammen aus dem gespeicherten Werkzeug, der Editor arbeitet auf
         * seinem eigenen Zustand, und nach einem Umbenennen ist die Id das Verlässlichere.
         *
         * Gibt zurück, ob es ein Feld dieses Namens gab — der Aufrufer kann sonst stehen bleiben,
         * statt einen leeren Dialog zu zeigen.
         */
        feldBearbeiten: (kennzeichen: string): boolean => {
            const gesucht = String(kennzeichen ?? '').trim();
            if (!gesucht) return false;
            const feld =
                fields.find(kandidat => kandidat.id === gesucht) ??
                fields.find(kandidat => kandidat.name === gesucht);
            if (!feld) return false;
            editField(feld);
            return true;
        },
    }), [fields, debugName, editField]);

    const updateField = () => {
        if (editingField && newField.name && newField.type) {
            const updateFieldInList = (fieldList: ExtendedZodField[]): ExtendedZodField[] => {
                return fieldList.map(field => {
                    if (field.id === editingField.id) {
                        return {
                            ...field,
                            name: newField.name!,
                            type: newField.type as ZodField['type'],
                            required: newField.required || false,
                            description: newField.description || '',
                            defaultValue: newField.defaultValue || '',
                            isArray: newField.isArray,
                            arrayType: newField.arrayType as ZodField['arrayType'],
                            fileType: newField.fileType,
                            templateFile: newField.templateFile,
                            hasPopulationInstructions: newField.hasPopulationInstructions || false
                        };
                    } else if (field.children) {
                        return {
                            ...field,
                            children: updateFieldInList(field.children)
                        };
                    }
                    return field;
                });
            };
            
            setFields(updateFieldInList(fields));
            setEditingField(null);
            setNewField({});
            setAddFieldDialogOpen(false);
            triggerOnChange();
        }
    };

    const deleteField = (fieldId: string) => {
        const removeFieldFromList = (fieldList: ExtendedZodField[]): ExtendedZodField[] => {
            return fieldList.filter(field => {
                if (field.id === fieldId) {
                    return false;
                } else if (field.children) {
                    field.children = removeFieldFromList(field.children);
                }
                return true;
            });
        };
        
        setFields(removeFieldFromList(fields));
        triggerOnChange();
    };

    const renderField = (field: ExtendedZodField, depth: number = 0) => {
        const fieldType = fieldTypes.find(ft => ft.key === field.type);
        const FieldIcon = fieldType?.Icon ?? Type;

        return (
            <div
                key={field.id}
                data-field-id={field.id}
                className={cn(
                    'field-item mb-1 rounded-md border bg-muted/40 p-2 text-sm',
                    dragOverField === field.id && 'drag-over border-primary bg-accent',
                )}
                style={{ marginLeft: `${depth * 20}px` }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
            >
                <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <FieldIcon className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="font-bold">{field.name}</span>
                        <span className="text-muted-foreground">({field.type})</span>
                        {field.required && <span className="text-destructive">*</span>}
                        {field.type === 'array' && field.arrayType && field.arrayType !== 'object' && (
                            <span className="text-primary">[{field.arrayType}]</span>
                        )}
                        {field.type === 'array' && field.arrayType === 'object' && (
                            <span className="text-primary">[object]</span>
                        )}
                        {field.type === 'array' && !field.arrayType && (
                            <span className="text-warning">[unset]</span>
                        )}
                        {/* Auch an einer Liste von Dateien: `[file] (pdf)` sagt, was gemeint ist. */}
                        {(field.type === 'file' || field.arrayType === 'file') &&
                            field.fileType &&
                            field.fileType !== 'any' && (
                                <span className="text-primary">({field.fileType})</span>
                            )}
                    </div>
                    <div className="flex shrink-0 gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => editField(field)}
                            title={t('ui.edit')}
                            aria-label={t('ui.edit')}
                        >
                            <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => deleteField(field.id)}
                            title={t('ui.delete')}
                            aria-label={t('ui.delete')}
                        >
                            <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                    </div>
                </div>
                {field.description && (
                    <div className="mt-1 text-xs text-muted-foreground">
                        {field.description}
                    </div>
                )}
                {field.defaultValue && (
                    <div className="mt-0.5 text-xs italic text-primary">
                        {field.type === 'array' ? (
                        <>Default: {parseArrayDefaultValue(field.defaultValue).length} Werte</>
                    ) : (
                        field.type === 'file' ? (
                            <>Default: {getDisplayName(field.defaultValue)}</>
                        ) : (
                        <>Default: {field.defaultValue}</>
                        ))}
                    </div>
                )}
                {field.templateFile && (
                    <div className="mt-0.5 flex items-center gap-1 text-xs italic text-primary">
                        <FileSpreadsheet className="h-3 w-3 shrink-0" aria-hidden />
                        Vorlage: {getDisplayName(field.templateFile)}
                    </div>
                )}
                {field.hasPopulationInstructions && (
                    <div className="mt-0.5 flex items-center gap-1 text-xs italic text-success">
                        <Zap className="h-3 w-3 shrink-0" aria-hidden />
                        Population Instructions
                    </div>
                )}
                {field.children && field.children.length > 0 && (
                    <div className="mt-2">
                        {field.children.map(child => renderField(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    // Track the current prompt tool ID to know when we switch between different tools
    const [currentPromptToolId, setCurrentPromptToolId] = React.useState<string | null>(null);
    
    // Update fields when initialFields changes (only when switching between different prompt tools)
    React.useEffect(() => {
        // Check if we're switching to a different prompt tool
        const newPromptToolId = initialFields ? JSON.stringify(initialFields) : null;
        
        if (newPromptToolId !== currentPromptToolId) {
            console.log('ZodEditor: Switching to different prompt tool, updating fields');
            setCurrentPromptToolId(newPromptToolId);
            // Convert the fields through convertToExtended to preserve fileType
            const convertedFields = initialFields?.map(convertToExtended) || [];
            console.log('ZodEditor: Converted fields for update:', JSON.stringify(convertedFields, null, 2));
            setFields(convertedFields);
        }
    }, [initialFields, currentPromptToolId]);

    // Schema generation is now handled by getCurrentSchema() method called on demand

    // Auto-select text in name field when dialog opens
    React.useEffect(() => {
        if (addFieldDialogOpen) {
            setTimeout(() => {
                if (nameFieldRef.current) {
                    nameFieldRef.current.select();
                }
            }, 100);
        }
    }, [addFieldDialogOpen]);

    // Helper to check if current field type is a file type (single file or file array)
    const isFileFieldType = newField.type === 'file' || (newField.type === 'array' && newField.arrayType === 'file');

    // Helper to get filename for display (looks up from map, falls back to truncated ID)
    const getDisplayName = (id: string): string => {
        if (!id) return '';
        // Look up filename from map
        const filename = fileIdToName.get(id);
        if (filename) return filename;
        // The referenced ManagedFile no longer exists (resolved to 404).
        if (missingFileIds.has(id)) {
            return t('ui.fileNotFound', '(Datei nicht gefunden)');
        }
        // Fallback: truncate long IDs for display (still resolving)
        if (id.length > 20) {
            return `${id.substring(0, 8)}...${id.substring(id.length - 6)}`;
        }
        return id;
    };
    
    // Helper to store file ID -> filename mapping
    const storeFileMapping = (id: string, name: string) => {
        setFileIdToName(prev => {
            const newMap = new Map(prev);
            newMap.set(id, name);
            return newMap;
        });
    };

    // Display component for selected file default value (single file)
    const FileDefaultValueDisplay = ({ value, onChange, label }: { value: string; onChange: (value: string) => void; label: string }) => {
        const displayValue = getDisplayName(value);

        return (
            <div className="mt-2 rounded-md border bg-muted/40 p-2">
                <div className="mb-1 text-xs font-semibold">{label}</div>
                {value ? (
                    <div className="flex items-center gap-2 rounded-md bg-muted p-2">
                        <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                        <span className="min-w-0 flex-1 break-all text-sm">{displayValue}</span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            onClick={() => onChange('')}
                            title={t('ui.clear')}
                            aria-label={t('ui.clear')}
                        >
                            <X className="h-4 w-4" aria-hidden />
                        </Button>
                    </div>
                ) : (
                    <div className="p-3 text-center text-xs text-muted-foreground">
                        {t('ui.clickFileInTree', 'Click a file in the tree to select')}
                    </div>
                )}
            </div>
        );
    };

    // Display component for template file (for Excel output fields)
    const TemplateFileDisplay = ({ value, onChange, label }: { value: string; onChange: (value: string) => void; label: string }) => {
        const displayValue = getDisplayName(value);

        return (
            <div className="mt-2 rounded-md border bg-muted/40 p-2">
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold">
                    <FileSpreadsheet className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {label}
                </div>
                {value ? (
                    <div className="flex items-center gap-2 rounded-md bg-muted p-2">
                        <FileSpreadsheet className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                        <span className="min-w-0 flex-1 break-all text-sm">{displayValue}</span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            onClick={() => onChange('')}
                            title={t('ui.clear')}
                            aria-label={t('ui.clear')}
                        >
                            <X className="h-4 w-4" aria-hidden />
                        </Button>
                    </div>
                ) : (
                    <div className="p-3 text-center text-xs text-muted-foreground">
                        {t('ui.clickExcelTemplateInTree', 'Klicken Sie auf eine Excel-Datei im Baum, um sie als Vorlage zu verwenden')}
                    </div>
                )}
            </div>
        );
    };

    // Helper to parse JSON array from defaultValue string
    const parseArrayDefaultValue = (value: string): string[] => {
        if (!value) return [];
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            // Fallback: if it's not valid JSON, return empty array
            return [];
        }
    };

    // Helper to stringify array for defaultValue
    const stringifyArrayDefaultValue = (files: string[]): string => {
        return JSON.stringify(files);
    };

    // Display component for multiple file selection (for arrays)
    const FileArrayDefaultValueDisplay = ({ value, onChange, label }: { value: string; onChange: (value: string) => void; label: string }) => {
        // Parse JSON array from value
        const files = parseArrayDefaultValue(value);

        const removeFile = (path: string) => {
            const newFiles = files.filter(f => f !== path);
            onChange(newFiles.length > 0 ? stringifyArrayDefaultValue(newFiles) : '');
        };

        return (
            <div className="mt-2 rounded-md border bg-muted/40 p-2">
                <div className="mb-1 text-xs font-semibold">
                    {label} ({files.length} {files.length === 1 ? t('ui.file', 'file') : t('ui.files', 'files')})
                </div>
                {files.length > 0 ? (
                    <div className="flex flex-col gap-1">
                        {files.map((fileId, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-2 rounded-md bg-muted px-2 py-1.5"
                            >
                                <FileText className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                                <span className="min-w-0 flex-1 break-all text-xs">{getDisplayName(fileId)}</span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 shrink-0"
                                    onClick={() => removeFile(fileId)}
                                    title={t('ui.remove')}
                                    aria-label={t('ui.remove')}
                                >
                                    <X className="h-3.5 w-3.5" aria-hidden />
                                </Button>
                            </div>
                        ))}
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="mt-1 self-start"
                            onClick={() => onChange('')}
                        >
                            <X className="mr-2 h-4 w-4" aria-hidden />
                            {t('ui.clearAll', 'Clear all')}
                        </Button>
                    </div>
                ) : (
                    <div className="p-3 text-center text-xs text-muted-foreground">
                        {t('ui.clickFilesToSelect', 'Click files in the tree to add them')}
                    </div>
                )}
            </div>
        );
    };

    // Expose addFileToArray for the FileTreePicker callback - adds file ID to array
    const addFileToArrayDefault = (id: string, name: string) => {
        // Store the ID -> filename mapping for display
        storeFileMapping(id, name);
        
        setNewField(prevField => {
            const fileIds = parseArrayDefaultValue(prevField.defaultValue || '');
            if (!fileIds.includes(id)) {
                const newFileIds = [...fileIds, id];
                return { ...prevField, defaultValue: stringifyArrayDefaultValue(newFileIds) };
            }
            return prevField;
        });
    };

    return (
        <div>
            <div className="mb-4">
                {label && <div className="mb-2 text-sm">{label}</div>}
                <div className="flex flex-wrap gap-5">
                    <div className="field-types-panel w-[150px] shrink-0 rounded-md border bg-muted/40 p-2">
                        <div className="mb-2 text-sm font-bold">{t('ui.fieldTypes')}</div>
                        {fieldTypes.map(type => (
                            <div
                                key={type.key}
                                draggable
                                onDragStart={(e) => handleDragStart(e, type.key)}
                                className="mb-1 flex cursor-grab items-center gap-1.5 rounded-md border bg-background p-1.5 text-xs"
                            >
                                <type.Icon className="h-3 w-3 shrink-0" aria-hidden />
                                <span>{type.text}</span>
                            </div>
                        ))}
                    </div>

                    <div
                        ref={dropZoneRef}
                        className={cn(
                            'drop-zone min-h-[150px] min-w-[280px] flex-1 rounded-md border-2 border-dashed border-input p-3',
                            isDragging ? 'bg-accent' : 'bg-background',
                        )}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragLeave={handleDragLeave}
                    >
                        {fields.length === 0 ? (
                            <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
                                <Upload className="mb-1.5 h-5 w-5" aria-hidden />
                                <div className="text-xs">{t('ui.dragFieldTypesHereOr')}</div>
                                <Button type="button" variant="ghost" onClick={() => setAddFieldDialogOpen(true)}>
                                    {t('ui.addField')}
                                </Button>
                            </div>
                        ) : (
                            <div>
                                <div className="mb-4 flex items-center justify-between gap-2">
                                    <div className="text-sm font-bold">{t('ui.schemaFields')}</div>
                                    <Button type="button" variant="ghost" onClick={() => setAddFieldDialogOpen(true)}>
                                        {t('ui.addField')}
                                    </Button>
                                </div>
                                {fields.map(field => renderField(field))}
                            </div>
                        )}
                    </div>

                    <div className="zod-schema-panel min-w-[280px] flex-1 rounded-md border bg-muted/40 p-2">
                        <div className="mb-2 text-sm font-bold">{t('ui.generatedSchema')}</div>
                        <Textarea
                            aria-label={t('ui.generatedSchema')}
                            rows={12}
                            value={generateZodSchema(fields)}
                            readOnly
                            className="bg-background font-mono text-xs"
                        />
                    </div>
                </div>
            </div>

            <Dialog
                open={addFieldDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setAddFieldDialogOpen(false);
                        setEditingField(null);
                        setNewField({});
                    }
                }}
            >
                <DialogContent
                    className={cn(
                        'max-h-[85vh] overflow-y-auto',
                        isFileFieldType
                            ? 'max-w-[min(960px,96vw)]'
                            : 'max-w-[calc(100vw-2rem)] sm:max-w-[500px]',
                    )}
                    onInteractOutside={(event) => event.preventDefault()}
                >
                <DialogHeader>
                    <DialogTitle>{editingField ? t('ui.editField') : t('ui.addNewField')}</DialogTitle>
                </DialogHeader>
                <div className="mt-5 flex min-w-0 flex-col gap-6 md:flex-row">
                    {/* Left side - Form fields */}
                    <div className={cn('flex min-w-0 flex-col gap-3', isFileFieldType ? 'md:w-[400px] md:shrink-0' : 'flex-1')}>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="zod-field-name">{t('ui.fieldNameOptional')}</Label>
                            <Input
                                id="zod-field-name"
                                value={newField.name || ''}
                                onChange={(event) => setNewField({ ...newField, name: event.target.value })}
                                placeholder={t('ui.leaveEmptyForAutoName')}
                                ref={nameFieldRef}
                            />
                        </div>
                        <ComboBoxField
                            id="zod-field-type"
                            label={t('ui.fieldType')}
                            selectedKey={newField.type}
                            options={fieldTypes.map(type => ({ key: type.key, text: type.text }))}
                            onSelect={(key) => setNewField({ ...newField, type: key as ZodField['type'], defaultValue: '' })}
                            placeholder={t('ui.pleaseSelect')}
                            emptyText={t('ui.noOptions')}
                            required
                        />
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="zod-field-description">{t('ui.descriptionOptional')}</Label>
                            <Textarea
                                id="zod-field-description"
                                value={newField.description || ''}
                                onChange={(event) => setNewField({ ...newField, description: event.target.value })}
                                rows={2}
                            />
                        </div>
                        {(newField.type === 'string' || newField.type === 'number' || newField.type === 'boolean' || newField.type === 'url') && (
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="zod-field-default-value">{t('ui.defaultValueOptional')}</Label>
                                <Input
                                    id="zod-field-default-value"
                                    value={newField.defaultValue || ''}
                                    onChange={(event) => setNewField({ ...newField, defaultValue: event.target.value })}
                                    placeholder={newField.type === 'string' ? t('ui.exampleString') : newField.type === 'number' ? t('ui.exampleNumber') : newField.type === 'boolean' ? t('ui.exampleBoolean') : ''}
                                />
                            </div>
                        )}
                        {/*
                          * Auch für eine LISTE von Dateien: Wer `array[file]` wählte, hatte bis
                          * hierher keine Möglichkeit mehr, den Dateityp zu setzen — die Auswahl
                          * hing an `type === 'file'`, obwohl der Schema-Erzeuger den Typ für Listen
                          * längst auswertet (`z.array(z.custom(…))` weiter oben).
                          */}
                        {isFileFieldType && (
                            <div>
                                <ComboBoxField
                                    id="zod-field-file-type"
                                    label={t('ui.fileType')}
                                    selectedKey={newField.fileType || 'any'}
                                    options={[
                                        { key: 'any', text: 'Any File' },
                                        { key: 'markdown', text: 'Markdown (.md)' },
                                        { key: 'text', text: 'Text Files' },
                                        { key: 'image', text: 'Image Files' },
                                        { key: 'pdf', text: 'PDF Files' },
                                        { key: 'excel', text: t('ui.excelFile') },
                                        { key: 'word', text: t('ui.wordFile') },
                                        { key: 'powerpoint', text: t('ui.powerpointFile') }
                                    ]}
                                    placeholder={t('ui.pleaseSelect')}
                                    emptyText={t('ui.noOptions')}
                                    onSelect={(key) => {
                                        console.log(`🔍 File type selected:`, key);
                                        if (key) {
                                            const isExcel = key === 'excel';
                                            const updatedField = {
                                                ...newField,
                                                fileType: key as 'markdown' | 'text' | 'image' | 'pdf' | 'excel' | 'word' | 'powerpoint' | 'any',
                                                // Clear templateFile when switching away from excel
                                                templateFile: isExcel ? newField.templateFile : '',
                                                // Clear defaultValue for output Excel files (they only use templateFile)
                                                defaultValue: (isOutputSchema && isExcel) ? '' : newField.defaultValue
                                            };
                                            console.log(`🔍 Updated newField:`, updatedField);
                                            setNewField(updatedField);
                                        }
                                    }}
                                />
                                {/* For output Excel files, only show template file picker (no default value) */}
                                {newField.type === 'file' && isOutputSchema && newField.fileType === 'excel' ? (
                                    <TemplateFileDisplay
                                        value={newField.templateFile || ''}
                                        onChange={(value) => setNewField({ ...newField, templateFile: value })}
                                        label={t('ui.templateFileOptional', 'Vorlagen-Datei (optional)')}
                                    />
                                ) : newField.type === 'file' ? (
                                    <FileDefaultValueDisplay
                                        value={newField.defaultValue || ''}
                                        onChange={(value) => setNewField({ ...newField, defaultValue: value })}
                                        label={t('ui.defaultValueOptional')}
                                    />
                                ) : null}
                                <div className="mt-3 flex items-center gap-2">
                                    <Checkbox
                                        id="zod-field-population-instructions"
                                        checked={newField.hasPopulationInstructions || false}
                                        onCheckedChange={(checked) => setNewField({ ...newField, hasPopulationInstructions: checked === true })}
                                    />
                                    <Label htmlFor="zod-field-population-instructions" className="cursor-pointer font-normal">
                                        {t('ui.hasPopulationInstructions', 'Has Population Instructions')}
                                    </Label>
                                </div>
                            </div>
                        )}
                        <div className="mt-3 flex items-center gap-2">
                            <Checkbox
                                id="zod-field-required"
                                checked={newField.required || false}
                                onCheckedChange={(checked) => setNewField({ ...newField, required: checked === true })}
                            />
                            <Label htmlFor="zod-field-required" className="cursor-pointer font-normal">
                                {t('ui.required')}
                            </Label>
                        </div>
                        {newField.type === 'array' && (
                            <div>
                                <ComboBoxField
                                    id="zod-field-array-type"
                                    label={t('ui.arrayElementType')}
                                    selectedKey={newField.arrayType}
                                    options={[
                                        { key: 'string', text: 'String' },
                                        { key: 'number', text: 'Number' },
                                        { key: 'boolean', text: 'Boolean' },
                                        { key: 'object', text: 'Object' },
                                        { key: 'file', text: 'File' }
                                    ]}
                                    placeholder={t('ui.pleaseSelect')}
                                    emptyText={t('ui.noOptions')}
                                    onSelect={(key) => setNewField({ ...newField, arrayType: key as ZodField['arrayType'], defaultValue: '' })}
                                    required
                                />
                                {newField.arrayType === 'file' && (
                                    <FileArrayDefaultValueDisplay
                                        value={newField.defaultValue || ''}
                                        onChange={(value) => setNewField({ ...newField, defaultValue: value })}
                                        label={t('ui.defaultValueOptional')}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right side - File tree picker (only for file types) */}
                    {isFileFieldType && (
                        <div className="min-w-0 flex-1">
                            {/* For output Excel files, only show template file picker */}
                            {isOutputSchema && newField.type === 'file' && newField.fileType === 'excel' ? (
                                <>
                                    <div className="mb-2 text-sm font-semibold">
                                        {t('ui.selectTemplateFile', 'Vorlagen-Datei auswählen')}
                                    </div>
                                    <div className="mb-2 text-xs text-muted-foreground">
                                        {t('ui.clickExcelTemplateToSelect', 'Klicken Sie auf eine Excel-Datei, um sie als Vorlage für die Ausgabe zu verwenden')}
                                    </div>
                                    <FileTreePicker
                                        fileTypeFilter="excel"
                                        onFileSelect={(id, name) => {
                                            storeFileMapping(id, name);
                                            setNewField({ ...newField, templateFile: id });
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="mb-2 text-sm font-semibold">
                                        {t('ui.selectFile', 'Datei auswählen')}
                                    </div>
                                    <div className="mb-2 text-xs text-muted-foreground">
                                        {newField.type === 'array' && newField.arrayType === 'file'
                                            ? t('ui.clickFilesToAdd', 'Klicken Sie auf Dateien, um sie zur Liste hinzuzufügen')
                                            : t('ui.clickFileToSelect', 'Klicken Sie auf eine Datei, um sie als Standardwert festzulegen')
                                        }
                                    </div>
                                    <FileTreePicker
                                        fileTypeFilter={newField.fileType || (newField.arrayType === 'file' ? 'any' : undefined)}
                                        onFileSelect={(id, name) => {
                                            // Store the ID -> filename mapping for display
                                            storeFileMapping(id, name);

                                            // For arrays, add to the list; for single files, replace
                                            // Stores file ID as default value, but displays filename
                                            if (newField.type === 'array' && newField.arrayType === 'file') {
                                                addFileToArrayDefault(id, name);
                                            } else {
                                                setNewField({ ...newField, defaultValue: id });
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button type="button" variant="outline" onClick={() => {
                        setAddFieldDialogOpen(false);
                        setEditingField(null);
                        setNewField({});
                    }}>{t('ui.cancel')}</Button>
                    <Button type="button" onClick={editingField ? updateField : addField}>{editingField ? t('ui.update') : t('ui.addField')}</Button>
                </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
});

ZodEditor.displayName = 'ZodEditor';

export default ZodEditor;